<template>
  <v-card flat>
    <v-container class="px-6 pt-6 purtrak-scrollable" fluid style="height:calc(100vh - 206px)">
      <v-row v-if="company.status==='Inactive'">
        <v-col cols="1">
          <v-checkbox label="Activate" v-model="activate" @change="setDirty(true)"/>
        </v-col>
      </v-row>
      <v-row align="center">
        <v-col cols="8" lg="2">
          <v-combobox label="Plan" v-model="plan" outlined dense hide-details :items="['Essentials','Plus']"
                      :disabled="company.status==='Inactive' && !activate" @change="setDirty(true);planChanged=true"/>
        </v-col>
        <v-col offset-lg="2">
          {{planAmount | currency}} / month
        </v-col>
      </v-row>


      <v-row align="center" class="mt-4">
        <v-col cols="8" lg="4">
          <v-select label="Add-ons" v-model="addOns" outlined hide-details multiple small-chips deletable-chips
                      :items="addOnList" no-data-text=""
                      :disabled="company.status==='Inactive' && !activate" @change="setDirty(true);planChanged=true">
            <template v-slot:item="{item}">
              <v-list-item-content>{{item.text}}</v-list-item-content>
              <v-list-item-action class="text-caption">{{item.price | currency}} / month</v-list-item-action>
            </template>
          </v-select>
        </v-col>
        <v-col>
          {{addOnAmount | currency}} / month
        </v-col>
      </v-row>


      <v-row class="mt-4">
        <v-col cols="8" lg="4">
          <template v-if="company.status==='Trial'">Trial expires</template>
          <template v-else>Current payment period ends</template>
          {{company.paymentPeriodEnd | formatDate}}
        </v-col>
        <v-col>Total {{planAmount+addOnAmount | currency}} / month (+ GST/HST)</v-col>
      </v-row>
      <v-row v-if="planChanged">
        <v-col>Changing your plan or add-ons will cause your <strong>next</strong> bill to be appropriately prorated</v-col>
      </v-row>

      <v-row v-if="currentPayment !== null" class="mt-4">
        <v-col cols="12" sm="8" lg="4">
          {{currentPayment.brand}}
          **** **** ***** {{currentPayment.last4}}
          {{currentPayment.expMonth}}/{{currentPayment.expYear}}
          <v-btn small color="primary" class="ml-4" @click="activate=true;setDirty(true);change()">Change</v-btn>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12" sm="8" lg="4">
          <div ref="stripe" class="mt-4 purtrak-cc"/>
          <div ref="errors" class="error--text"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn v-if="company.status!=='Inactive'" color="warning" outlined @click="confirmDeactivate=true" small class="mr-4 mt-4">Deactivate</v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-divider/>
    <v-card-actions class="pr-6">
      <v-spacer/>
      <v-btn plain color="primary" @click="load">Reset</v-btn>
      <v-btn color="primary" @click="save" :disabled="!dirty" :loading="saving">Save</v-btn>
    </v-card-actions>

    <v-dialog v-model="confirmDeactivate" persistent max-width="450">
      <v-card>
        <v-card-title>Deactivate</v-card-title>
        <v-divider/>
        <v-card-text class="pt-4 body-1">
          Are you sure you want to deactivate the distillery?
          <div class="mt-4 warning--text">
            If you deactivate, distillery information will no longer be accessible.
            (The distillery can be re-activated by updating the billing information.)
          </div>
          <div class="mt-4">
            Your distillery will be deactivated immediately and you will no longer
            be billed after the end of the current billing period.
          </div>
        </v-card-text>
        <v-divider/>
        <v-card-actions class="pr-6">
          <v-spacer></v-spacer>
          <v-btn color="warning" plain @click="deactivate">Deactivate</v-btn>
          <v-btn color="primary" @click="confirmDeactivate=false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style scoped>
.purtrak-cc {
  height:40px;
  border:1px solid #cccccc;
  border-radius:4px;
  padding:10px;
  font-family:system-ui;
  color:#252525;
  font-size:14px;
}
</style>

<script>
import {loadStripe} from '@stripe/stripe-js/pure';

export default {
  name: 'SettingsAccount',
  data() {
    return {
      plan: null,
      addOnList: [
        {text:'Shopify',value:'Shopify',price:25},
        {text:'Square',value:'Square',price:25},
//        {text:'Wix',value:'Wix',price:50},
        {text:'Customer portal',value:'Portal',price:100}
      ],
      addOns: [],
      stripe: null,
      card: null,
      currentPayment: null,
      saving: false,
      confirmDeactivate: false,
      activate: false,
      planChanged: false,
      dirty: false
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    planAmount() {
      return {Essentials:150,Plus:200,Premium:250}[this.plan];
    },
    addOnAmount() {
      return this.addOns.reduce((total, addOn) => {
        const a = this.addOnList.find(a => a.value === addOn);
        return total + (a ? a.price : 0);
      }, 0);
    }
  },
  async mounted() {
    this.load();

    // Create an instance of Elements
    this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PK);
    const elements = this.stripe.elements();

    const style = {
      base: {
        fontSize: '14px',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#f00',
        iconColor: '#f00'
      }
    };

    // Create an instance of the card Element
    this.card = elements.create('card', {style: style});

    // Add an instance of the card Element into the `card-element` <div>
    if (!this.currentPayment)
      this.card.mount(this.$refs.stripe)

    // Handle real-time validation errors from the card Element.
    this.card.addEventListener('change', this.cardChangedEvent);
  },
  beforeUnmount() {
    window.removeEventListener('change', this.cardChangedEvent);
    if (this.card)
      this.card.destroy();
  },
  methods: {
    setDirty(dirty) {
      this.dirty = dirty;
      this.$emit('dirty',dirty);
    },
    async load() {
      this.activate = false;
      const results = await this.$apiCall(`query SettingsAccount {
                                             company {status, paymentPeriodEnd, plan, addOns}
                                             companyPayment {brand,last4,expMonth,expYear}
                                           }`,
                                          {});
      this.plan = results.data.company.plan;
      this.addOns = results.data.company.addOns;
      this.currentPayment = results.data.companyPayment;
      if (this.currentPayment)
        Object.assign(this.company, results.data.company);   // Get updated status/paymentPeriodEnd

      this.setDirty(false);
    },
    cardChangedEvent(event) {
      const errorElement = this.$refs.errors;
      if (event.error)
        errorElement.textContent = event.error.message;
      else
        errorElement.textContent = '';
      this.setDirty(true);
    },
    change() {
      this.currentPayment = null;
      this.setDirty(true);
      this.$nextTick(() => {
        this.card.mount(this.$refs.stripe);
      });
    },
    async save() {
      this.saving = true;
      let results = {};

      // Handle credit card info
      if (this.currentPayment == null && !this.card._empty) {
        const stripeResult = await this.stripe.createToken(this.card);
        if (stripeResult.error) {
          this.$refs.errors.textContent = stripeResult.error.message;
          this.saving = false;
          return;
        }
        results = await this.$apiCall(`mutation SettingsAccount($paymentToken:String!) {
                                         companySetPayment(paymentToken:$paymentToken) {id}
                                       }`,
                                       {paymentToken:stripeResult.token.id});
      }

      // Activate company with plan (only if it was inactive and we chose to activate it)
      if (!results.error && this.activate) {
        results = await this.$apiCall(`mutation SettingsAccount($plan:Plan!,$addOns:[String]!) {
                                          companyActivate(plan:$plan, addOns:$addOns) {status,maxUsers,allowMultipleSites,allowAccounting,allowPO,allowCSVExport,allowWine,allowApprovedFormulation,allowAudit,allowPlanning,addOns}
                                        }`,
                                        {plan:this.plan,addOns:this.addOns});
        if (!results.error)
          Object.assign(this.company, results.data.companyActivate);
      }

      // Set plan if not activating
      else if (!results.error) {
        results = await this.$apiCall(`mutation SettingsAccount($plan:Plan,$addOns:[String]!) {
                                         companySetPlan(plan:$plan, addOns:$addOns) {maxUsers,allowMultipleSites,allowAccounting,allowPO,allowCSVExport,allowWine,allowApprovedFormulation,allowAudit,allowPlanning,addOns}
                                       }`,
                                       {plan:this.plan,addOns:this.addOns});
        if (!results.error)
          Object.assign(this.company, results.data.companySetPlan);
      }

      this.saving = false;
      if (!results.error)
        this.setDirty(false);
    },
    async deactivate() {
      const results = await this.$apiCall(`mutation SettingsAccount {
                                              companyDeactivate {status}
                                            }`,
                                          {});
      if (!results.error) {
        this.company.status = results.data.companyDeactivate.status;
        this.confirmDeactivate = false;
        this.load();
      }
    },
  }
}
</script>